import { defineStore } from "pinia";
import { computed, reactive, ref, watch } from "vue";

import {
  archiveApplication as archiveApplicationRequest,
  getTrackingOverview,
  updateTrackingStatus as updateTrackingStatusRequest,
} from "@/api/tracking";
import { invalidateCompanyWishlistCache } from "@/cache/companyWishlistCache";
import { useUserStore } from "@/stores/user";
import { ApiError, type AsyncStatus } from "@/types/api";
import type { Application, TrackingStatus } from "@/types/application";

interface ApplicationFilters {
  company: string;
  role_title: string;
  location: string;
  tracking_status: string;
  status: string;
}

function normalize(value: unknown): string {
  return String(value ?? "").trim().replace(/\s+/g, " ").toLocaleLowerCase();
}

function formatLabel(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toLocaleUpperCase()}${part.slice(1)}`)
    .join(" ");
}

function matches(value: unknown, filter: string, includeFormattedLabel = false): boolean {
  const needle = normalize(filter);
  if (!needle) return true;
  const candidates = includeFormattedLabel ? [value, formatLabel(value)] : [value];
  return candidates.some((candidate) => normalize(candidate).includes(needle));
}

function cleanOptions(values: unknown[]): string[] {
  return [...new Set(values.map((value) => String(value ?? "").trim()).filter(Boolean))].sort(
    (left, right) => left.localeCompare(right),
  );
}

function normalizeError(caught: unknown, fallback: string): ApiError {
  return caught instanceof ApiError ? caught : new ApiError(fallback);
}

const REVIEW_WORKFLOW_STATUSES = new Set([
  "review_ready",
  "ready_for_user_submission",
  "needs_review",
]);

export const useApplicationsStore = defineStore("applications", () => {
  const userStore = useUserStore();
  const applications = ref<Application[]>([]);
  const statuses = ref<TrackingStatus[]>([]);
  const loadStatus = ref<AsyncStatus>("idle");
  const isRefreshing = ref(false);
  const error = ref<ApiError | null>(null);
  const filters = reactive<ApplicationFilters>({
    company: "",
    role_title: "",
    location: "",
    tracking_status: "",
    status: "",
  });
  const selectedApplicationIds = ref<string[]>([]);
  const pendingApplicationIds = ref<string[]>([]);
  const page = ref(1);
  const pageSize = ref(10);

  const totalCount = computed(() => applications.value.length);
  const selectedCount = computed(() => {
    const available = new Set(applications.value.map((application) => application.id));
    return selectedApplicationIds.value.filter((id) => available.has(id)).length;
  });
  const appliedCount = computed(
    () =>
      applications.value.filter((application) => {
        const status = normalize(application.tracking_status);
        return Boolean(status) && status !== "not_applied";
      }).length,
  );
  const inReviewCount = computed(
    () =>
      applications.value.filter((application) =>
        REVIEW_WORKFLOW_STATUSES.has(normalize(application.status)),
      ).length,
  );

  let loadController: AbortController | null = null;
  let activeLoadPreviousStatus: AsyncStatus | null = null;
  let loadGeneration = 0;
  let sessionGeneration = 0;
  const mutationTokens = new Map<string, symbol>();

  const filterOptions = computed(() => ({
    companies: cleanOptions(applications.value.map((application) => application.company)),
    roleTitles: cleanOptions(applications.value.map((application) => application.role_title)),
    locations: cleanOptions(applications.value.map((application) => application.location)),
    trackingStatuses: cleanOptions(
      [...statuses.value, ...applications.value.map((application) => application.tracking_status)].map(
        formatLabel,
      ),
    ),
    statuses: cleanOptions(applications.value.map((application) => formatLabel(application.status))),
  }));

  const filteredApplications = computed(() =>
    applications.value.filter(
      (application) =>
        matches(application.company, filters.company) &&
        matches(application.role_title, filters.role_title) &&
        matches(application.location, filters.location) &&
        matches(application.tracking_status, filters.tracking_status, true) &&
        matches(application.status, filters.status, true),
    ),
  );
  const totalPages = computed(() =>
    Math.max(1, Math.ceil(filteredApplications.value.length / pageSize.value)),
  );
  const pagedApplications = computed(() => {
    const offset = (page.value - 1) * pageSize.value;
    return filteredApplications.value.slice(offset, offset + pageSize.value);
  });
  const pageStart = computed(() =>
    filteredApplications.value.length ? (page.value - 1) * pageSize.value + 1 : 0,
  );
  const pageEnd = computed(() =>
    Math.min(page.value * pageSize.value, filteredApplications.value.length),
  );
  const selectedVisibleCount = computed(() => {
    const selected = new Set(selectedApplicationIds.value);
    return filteredApplications.value.filter((application) => selected.has(application.id)).length;
  });
  const allVisibleSelected = computed(
    () => filteredApplications.value.length > 0 && selectedVisibleCount.value === filteredApplications.value.length,
  );
  const someVisibleSelected = computed(
    () => selectedVisibleCount.value > 0 && !allVisibleSelected.value,
  );

  function clearFilters(): void {
    filters.company = "";
    filters.role_title = "";
    filters.location = "";
    filters.tracking_status = "";
    filters.status = "";
  }

  function setPage(value: number): void {
    if (!Number.isFinite(value)) {
      page.value = 1;
      return;
    }
    page.value = Math.min(Math.max(1, Math.trunc(value)), totalPages.value);
  }

  function setPageSize(value: number): void {
    pageSize.value = [10, 20, 50].includes(value) ? value : 10;
    page.value = 1;
  }

  watch(
    filters,
    () => {
      page.value = 1;
    },
    { deep: true, flush: "sync" },
  );
  watch(
    totalPages,
    (maximum) => {
      if (page.value > maximum) page.value = maximum;
    },
    { flush: "sync" },
  );

  function toggleSelection(applicationId: string): void {
    const selected = new Set(selectedApplicationIds.value);
    if (selected.has(applicationId)) selected.delete(applicationId);
    else selected.add(applicationId);
    selectedApplicationIds.value = [...selected];
  }

  function selectAllVisible(): void {
    const selected = new Set(selectedApplicationIds.value);
    if (allVisibleSelected.value) {
      for (const application of filteredApplications.value) selected.delete(application.id);
    } else {
      for (const application of filteredApplications.value) selected.add(application.id);
    }
    selectedApplicationIds.value = [...selected];
  }

  function clearSelection(): void {
    selectedApplicationIds.value = [];
  }

  function reconcileSelection(): void {
    const available = new Set(applications.value.map((application) => application.id));
    const reconciled = selectedApplicationIds.value.filter((id) => available.has(id));
    if (
      reconciled.length !== selectedApplicationIds.value.length ||
      reconciled.some((id, index) => id !== selectedApplicationIds.value[index])
    ) {
      selectedApplicationIds.value = reconciled;
    }
  }

  function ownsLoad(controller: AbortController, generation: number, session: number): boolean {
    return (
      loadController === controller &&
      loadGeneration === generation &&
      sessionGeneration === session &&
      !controller.signal.aborted
    );
  }

  function supersededLoadError(): Error {
    const error = new Error("Tracking load was superseded.");
    error.name = "AbortError";
    return error;
  }

  async function load(signal?: AbortSignal): Promise<Application[]> {
    loadController?.abort();
    const controller = new AbortController();
    loadController = controller;
    const generation = ++loadGeneration;
    const session = sessionGeneration;
    const previousLoadStatus =
      loadStatus.value === "loading" ? (activeLoadPreviousStatus ?? "idle") : loadStatus.value;
    activeLoadPreviousStatus = previousLoadStatus;
    const refreshing = loadStatus.value === "success" || loadStatus.value === "empty";
    let callerAbortListener: (() => void) | undefined;
    if (signal) {
      callerAbortListener = () => controller.abort();
      if (signal.aborted) controller.abort();
      else signal.addEventListener("abort", callerAbortListener, { once: true });
    }
    isRefreshing.value = refreshing;
    if (!refreshing) loadStatus.value = "loading";
    error.value = null;
    try {
      const overview = await getTrackingOverview(controller.signal);
      if (!ownsLoad(controller, generation, session)) {
        if (signal && !signal.aborted && sessionGeneration === session) throw supersededLoadError();
        return applications.value;
      }
      applications.value = overview.applications;
      statuses.value = overview.statuses;
      loadStatus.value = applications.value.length ? "success" : "empty";
      reconcileSelection();
      return applications.value;
    } catch (caught: unknown) {
      if (!ownsLoad(controller, generation, session)) {
        if (signal && !signal.aborted && sessionGeneration === session) throw supersededLoadError();
        return applications.value;
      }
      const normalized = normalizeError(caught, "Tracking applications could not be loaded.");
      error.value = normalized;
      loadStatus.value = refreshing ? (applications.value.length ? "success" : "empty") : "error";
      throw normalized;
    } finally {
      if (signal && callerAbortListener) signal.removeEventListener("abort", callerAbortListener);
      if (loadGeneration === generation && sessionGeneration === session) {
        isRefreshing.value = false;
        if (controller.signal.aborted && loadStatus.value === "loading") {
          loadStatus.value = previousLoadStatus;
        }
      }
      if (loadController === controller) {
        loadController = null;
        activeLoadPreviousStatus = null;
      }
    }
  }

  function setPending(applicationId: string, pending: boolean): void {
    const ids = new Set(pendingApplicationIds.value);
    if (pending) ids.add(applicationId);
    else ids.delete(applicationId);
    pendingApplicationIds.value = [...ids];
  }

  function ownsMutation(applicationId: string, token: symbol, session: number): boolean {
    return sessionGeneration === session && mutationTokens.get(applicationId) === token;
  }

  async function updateTrackingStatus(
    applicationId: string,
    trackingStatus: TrackingStatus,
  ): Promise<Application | null> {
    if (pendingApplicationIds.value.includes(applicationId)) return null;
    const token = Symbol(applicationId);
    const session = sessionGeneration;
    mutationTokens.set(applicationId, token);
    setPending(applicationId, true);
    error.value = null;
    try {
      const updated = await updateTrackingStatusRequest(applicationId, trackingStatus);
      if (ownsMutation(applicationId, token, session)) {
        applications.value = applications.value.map((application) =>
          application.id === applicationId ? updated : application,
        );
      }
      return updated;
    } catch (caught: unknown) {
      const normalized = normalizeError(caught, "Tracking status could not be updated.");
      if (ownsMutation(applicationId, token, session)) {
        error.value = normalized;
        try {
          await load();
        } catch {
          if (ownsMutation(applicationId, token, session)) error.value = normalized;
        }
      }
      throw normalized;
    } finally {
      if (ownsMutation(applicationId, token, session)) {
        mutationTokens.delete(applicationId);
        setPending(applicationId, false);
      }
    }
  }

  async function archiveApplication(applicationId: string): Promise<void> {
    if (pendingApplicationIds.value.includes(applicationId)) return;
    const token = Symbol(applicationId);
    const session = sessionGeneration;
    const ownerId = userStore.user?.id ?? null;
    mutationTokens.set(applicationId, token);
    setPending(applicationId, true);
    error.value = null;
    try {
      await archiveApplicationRequest(applicationId);
      if (
        !ownsMutation(applicationId, token, session) ||
        (userStore.user?.id ?? null) !== ownerId
      ) return;
      invalidateCompanyWishlistCache(ownerId);
      await load();
      if (ownsMutation(applicationId, token, session)) reconcileSelection();
    } catch (caught: unknown) {
      const normalized = normalizeError(caught, "The application could not be archived.");
      if (ownsMutation(applicationId, token, session)) error.value = normalized;
      throw normalized;
    } finally {
      if (ownsMutation(applicationId, token, session)) {
        mutationTokens.delete(applicationId);
        setPending(applicationId, false);
      }
    }
  }

  function dispose(): void {
    loadGeneration += 1;
    loadController?.abort();
    loadController = null;
    if (loadStatus.value === "loading" && activeLoadPreviousStatus) {
      loadStatus.value = activeLoadPreviousStatus;
    }
    activeLoadPreviousStatus = null;
    isRefreshing.value = false;
  }

  function reset(): void {
    sessionGeneration += 1;
    mutationTokens.clear();
    dispose();
    applications.value = [];
    statuses.value = [];
    loadStatus.value = "idle";
    isRefreshing.value = false;
    error.value = null;
    clearFilters();
    page.value = 1;
    pageSize.value = 10;
    selectedApplicationIds.value = [];
    pendingApplicationIds.value = [];
  }

  return {
    applications,
    statuses,
    loadStatus,
    isRefreshing,
    error,
    filters,
    selectedApplicationIds,
    pendingApplicationIds,
    page,
    pageSize,
    totalCount,
    selectedCount,
    appliedCount,
    inReviewCount,
    filterOptions,
    filteredApplications,
    totalPages,
    pagedApplications,
    pageStart,
    pageEnd,
    selectedVisibleCount,
    allVisibleSelected,
    someVisibleSelected,
    load,
    clearFilters,
    setPage,
    setPageSize,
    toggleSelection,
    selectAllVisible,
    clearSelection,
    reconcileSelection,
    updateTrackingStatus,
    archiveApplication,
    dispose,
    reset,
  };
});
